import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import RequestSection from "./RequestSection";
import { requestsText } from "./unifiedText";
import "./UnifiedRequests.css";

// The dashboard's Requests: shortcut buttons, then "In Progress" and "Completed",
// each a compact one-column list with its own server-side paging. Every request is
// understood and handled from its own row (no details page, no menus, no redirects).
// It follows the dashboard's selected restaurant (no selector of its own), reads
// through the unified API and never executes anything itself: the row actions call
// each request system's own endpoint behind a confirmation.
export default function UnifiedRequests({ lang, selectedStoreId = "", branches = [], highlightIds = [], onChanged, onOpenRequestFile, shortcuts = null, reloadKey, onListState }) {
  const t = requestsText(lang);
  const { role } = useAccess();
  const [reloadToken, setReloadToken] = useState(0);

  // the page that hosts the list changed something it created elsewhere (e.g. the cashier sent a new request): read again
  const firstKey = useRef(true);
  useEffect(() => {
    if (firstKey.current) firstKey.current = false;
    else setReloadToken((value) => value + 1);
  }, [reloadKey]);

  const afterChange = useCallback(async () => {
    setReloadToken((value) => value + 1);
    await onChanged?.();
  }, [onChanged]);

  // What the two lists hold, for the host: "loading" | "error" | "empty" | "data" (never "empty" while anything is loading or failed).
  const [phases, setPhases] = useState({});
  const report = useCallback((group, phase, total) => setPhases((current) => (current[group]?.phase === phase && current[group]?.total === total ? current : { ...current, [group]: { phase, total } })), []);
  useEffect(() => {
    const lists = [phases.inProgress, phases.completed];
    const state = lists.some((item) => !item || item.phase === "loading") ? "loading" : lists.some((item) => item.phase === "error") ? "error" : lists.some((item) => item.total > 0) ? "data" : "empty";
    onListState?.(state);
  }, [phases, onListState]);

  const highlightKey = highlightIds.join(",");
  const shared = useMemo(() => ({ storeId: selectedStoreId, t, lang, branches, role, highlightIds, onChanged: afterChange, onOpenFile: onOpenRequestFile }), [selectedStoreId, t, lang, branches, role, highlightKey, afterChange, onOpenRequestFile]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="lud-ur">
      <header className="lud-ur-header">
        <h2>{t.title}</h2>
      </header>
      {shortcuts}
      <RequestSection group="inProgress" title={t.sections.inProgress} reloadToken={reloadToken} onReport={report} {...shared} />
      <RequestSection group="completed" title={t.sections.completed} reloadToken={reloadToken} onReport={report} {...shared} />
    </div>
  );
}
